import {
  FRAMEWORK_CONFIGS,
  type Framework,
  type FrameworkConfig,
  type Language,
  type PackageManager,
} from '~/lib/stores/techStackSelection';
import type { DropdownOption } from '~/components/ui/Dropdown';
import type { ChatHistoryItem } from '~/services/interfaceService';
import type { StoreFileAction } from '~/types/actions';
import * as Sentry from '@sentry/remix';
import axios from 'axios';

class FrameworkService {
  /**
   * Retrieves the full configuration object for a given framework.
   * @param framework The framework identifier.
   * @returns The configuration object for the specified framework.
   */
  getFrameworkConfig(framework: Framework): FrameworkConfig {
    return FRAMEWORK_CONFIGS[framework];
  }

  /**
   * Checks if a given framework is of type 'app'.
   * @param framework The framework identifier.
   * @returns `true` if the framework is an app framework, otherwise `false`.
   */
  isAppFramework(framework: Framework): boolean {
    return this.getFrameworkConfig(framework).type === 'app';
  }

  /**
   * Generates a list of dropdown options for all available frameworks.
   * This is used to populate UI elements like the framework selection dropdown.
   * @returns An array of `DropdownOption` objects.
   */
  getFrameworkOptions(): DropdownOption[] {
    return Object.entries(FRAMEWORK_CONFIGS).map(([value, config]) => ({
      value: value as Framework,
      label: config.label,
      icon: config.icon,
    }));
  }

  /**
   * Formats a framework identifier into a user-friendly display name.
   * @param framework The framework identifier.
   * @returns The formatted framework name.
   */
  formatFramework(framework?: Framework): string {
    if (!framework) {
      return '';
    }

    return FRAMEWORK_CONFIGS[framework]?.label || framework;
  }

  /**
   * Formats a language identifier into a user-friendly display name.
   * @param language The language identifier.
   * @returns The formatted language name.
   */
  formatLanguage(language?: Language): string {
    if (!language) {
      return '';
    }

    const languageMap: Record<Language, string> = {
      ts: 'TypeScript',
      js: 'JavaScript',
    };
    return languageMap[language] || language;
  }

  /**
   * Extracts the framework and language from a chat history item's file actions.
   * @param messageData The chat history item.
   * @returns An object containing the detected framework and language.
   */
  extractFrameworks(messageData: ChatHistoryItem): {
    framework: string;
    language: string;
  } {
    const result = {
      framework: 'unknown',
      language: 'unknown',
    };

    const packageJson = messageData.fileActions.find(
      (action: StoreFileAction) => action.type === 'file' && action.filePath === 'package.json',
    );

    const hasMainDart = messageData.fileActions.find(
      (action: StoreFileAction) => action.type === 'file' && action.filePath === 'main.dart',
    );

    const hasHtmlFile = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.endsWith('.html'),
    );

    const hasTailwindConfig = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('tailwind.config'),
    );

    const hasViteConfig = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('vite.config.ts'),
    );

    const hasAngularJson = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('angular.json'),
    );

    const hasTsConfig = messageData.fileActions.some(
      (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('tsconfig.json'),
    );

    // Check for Flutter first since it won't have package.json
    if (hasMainDart) {
      result.framework = 'flutter';
      result.language = 'dart';
    } else if (hasTsConfig) {
      result.language = 'ts';
    } else {
      result.language = 'js';
    }

    try {
      if (hasAngularJson) {
        result.framework = 'angular_css';
      } else if (!hasViteConfig && hasHtmlFile) {
        result.framework = hasTailwindConfig ? 'html_tailwind' : 'html_css';
      }

      if (!packageJson || typeof packageJson.content !== 'string') {
        return result;
      }

      const packageData = JSON.parse(packageJson.content);
      let hasShadcnConfig = false;

      // Check for React Native
      if (packageData.dependencies?.react && packageData.dependencies?.['react-native']) {
        result.framework = 'reactnative';
      }
      // Check for React and its UI libraries
      else if (packageData.dependencies?.react) {
        if (packageData.dependencies?.['@chakra-ui/react']) {
          result.framework = 'react_chakra';
        } else if (packageData.devDependencies?.tailwindcss) {
          hasShadcnConfig = messageData.fileActions.some(
            (action: StoreFileAction) =>
              (action.type === 'file' && action.filePath === 'components.json') ||
              (typeof action.content === 'string' &&
                action.content.includes('@radix-ui') &&
                action.content.includes('clsx')),
          );
        }

        result.framework = hasShadcnConfig ? 'react_shadcn' : 'react_tailwind';
      }
      // Check for Vue
      else if (packageData.dependencies?.vue) {
        result.framework = packageData.devDependencies?.tailwindcss ? 'vue_tailwind' : 'vue_css';
      }

      return result;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error from extractFrameworks',
          packageJsonContent: packageJson?.content,
        },
      });
      return result;
    }
  }

  /**
   * Detects the framework, language, and package manager from a GitHub repository's
   * file tree and package.json content for a given branch.
   */
  async detectFrameworkFromRepo(
    owner: string,
    repo: string,
    branch: string,
    accessToken: string,
  ): Promise<{ framework: Framework; language: Language; packageManager: PackageManager }> {
    const result: { framework: Framework; language: Language; packageManager: PackageManager } = {
      framework: 'react_tailwind',
      language: 'ts',
      packageManager: 'npm',
    };

    try {
      // Fetch the repo tree
      const { data } = await axios.get(
        `https://api.github.com/repos/${owner}/${repo}/git/trees/${branch}?recursive=1`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/vnd.github.v3+json',
          },
        },
      );

      const tree: Array<{ path: string; type: string }> = data.tree || [];
      const filePaths = tree.filter((item) => item.type === 'blob').map((item) => item.path);

      // Detect file presence
      const hasAngularJson = filePaths.some((p) => p.includes('angular.json'));
      const hasViteConfig = filePaths.some((p) => p.includes('vite.config.ts') || p.includes('vite.config.js'));
      const hasTailwindConfig = filePaths.some((p) => p.includes('tailwind.config'));
      const hasTsConfig = filePaths.some((p) => p === 'tsconfig.json' || p.endsWith('/tsconfig.json'));
      const hasHtmlFile = filePaths.some((p) => p.endsWith('.html'));
      const hasComponentsJson = filePaths.some((p) => p === 'components.json');

      // Detect package manager from lockfiles
      const hasYarnLock = filePaths.some((p) => p === 'yarn.lock');
      const hasPnpmLock = filePaths.some((p) => p === 'pnpm-lock.yaml');
      const hasBunLock = filePaths.some((p) => p === 'bun.lockb');

      if (hasYarnLock) {
        result.packageManager = 'yarn';
      } else if (hasPnpmLock) {
        result.packageManager = 'pnpm';
      } else if (hasBunLock) {
        result.packageManager = 'npm'; // bun is not in PackageManager type, fallback to npm
      }

      // Detect language from tsconfig
      if (hasTsConfig) {
        result.language = 'ts';
      } else {
        result.language = 'js';
      }

      // Angular detection (no package.json needed)
      if (hasAngularJson) {
        result.framework = 'angular_css';
        result.language = 'ts'; // Angular is always TypeScript
        return result;
      }

      // HTML detection (no vite config, has html files)
      if (!hasViteConfig && hasHtmlFile && !filePaths.some((p) => p === 'package.json')) {
        result.framework = hasTailwindConfig ? 'html_tailwind' : 'html_css';
        result.language = 'js';
        return result;
      }

      // Try to fetch and parse package.json
      const hasPackageJson = filePaths.some((p) => p === 'package.json');

      if (!hasPackageJson) {
        // No package.json — check if it's a plain HTML project
        if (hasHtmlFile) {
          result.framework = hasTailwindConfig ? 'html_tailwind' : 'html_css';
          result.language = 'js';
        }

        return result;
      }

      // Fetch package.json content via GitHub API (supports CORS, unlike raw.githubusercontent.com)
      const pkgResponse = await axios.get(
        `https://api.github.com/repos/${owner}/${repo}/contents/package.json?ref=${branch}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/vnd.github.v3+json',
          },
        },
      );

      // GitHub API returns base64-encoded content
      const pkgContent = atob(pkgResponse.data.content);
      const packageData = JSON.parse(pkgContent);

      // React Native
      if (packageData.dependencies?.react && packageData.dependencies?.['react-native']) {
        result.framework = 'reactnative';
        result.language = 'ts';
        return result;
      }

      // React variants
      if (packageData.dependencies?.react) {
        if (packageData.dependencies?.['@chakra-ui/react']) {
          result.framework = 'react_chakra';
        } else if (packageData.devDependencies?.tailwindcss || packageData.dependencies?.tailwindcss) {
          // Check for Shadcn (components.json + radix indicators)
          if (hasComponentsJson) {
            result.framework = 'react_shadcn';
          } else {
            result.framework = 'react_tailwind';
          }
        } else {
          result.framework = 'react_tailwind'; // React without specific UI lib
        }

        return result;
      }

      // Vue
      if (packageData.dependencies?.vue) {
        result.framework = 'vue_tailwind';
        return result;
      }

      // Angular (in case angular.json wasn't at root but deps exist)
      if (packageData.dependencies?.['@angular/core']) {
        result.framework = 'angular_css';
        result.language = 'ts';
        return result;
      }

      // HTML fallback if has html files but also has package.json
      if (!hasViteConfig && hasHtmlFile) {
        result.framework = hasTailwindConfig ? 'html_tailwind' : 'html_css';
        result.language = 'js';
        return result;
      }

      return result;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error detecting framework from repo',
          owner,
          repo,
          branch,
        },
      });
      console.error('[detectFrameworkFromRepo] Error:', error);
      return result;
    }
  }
}

export const frameworkService = new FrameworkService();
